import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  input,
  runInInjectionContext,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';

import { firstValueFrom } from 'rxjs';

import { filterDefined } from '@core/common-legacy';
import { UnassignMemberFromPositionDialogComponent } from '@core/tellent-positions-smart-ui';
import { UnassignMemberFromPositionDialogData } from '@core/tellent-positions-types';
import { TellentWebComponent } from '@core/tellent-types';
import { DialogWidth } from '@core/user-interface-legacy';

import { DialogWrapComponent } from '../dialog/dialog-wrap.component';

/**
 * Confirms removing a member from the position they currently hold.
 *
 * Unlike the confirm-change dialog, this one performs its own request, owns its spinner and raises
 * its own notifications, so there is nothing to do here beyond handing it the member and position it
 * describes. It closes with `{ success: true }` only once the request went through, and stays open
 * on failure — so an `undefined` result means the user backed out.
 */
@Component({
  selector: 'x-unassign-position-dialog',
  template: ``,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class TellentUnassignPositionDialogWrapComponent extends DialogWrapComponent<UnassignMemberFromPositionDialogComponent> {
  private readonly injector = inject(Injector);

  public readonly component = UnassignMemberFromPositionDialogComponent;
  public readonly config = {
    width: DialogWidth.SmallXX,
    containerOptions: { fullscreenOnMobile: true },
  };

  public memberGuid = input<string>();
  public memberFirstName = input<string>();
  public memberLastName = input<string>();
  public memberEmail = input<string>();
  public memberAvatarUrl = input<string | null>();
  public positionGuid = input<string>();
  public positionName = input<string>();

  public override async resolveDialogData(): Promise<UnassignMemberFromPositionDialogData> {
    const memberGuid = await runInInjectionContext(this.injector, () =>
      firstValueFrom(toObservable(this.memberGuid).pipe(filterDefined)),
    );

    // Several of the remaining inputs are meaningful when empty, and the host sets every property in
    // one pass without a defined order, so they may still be unset when the guid resolves. Defer a
    // microtask to let that pass land.
    return new Promise(resolve =>
      queueMicrotask(() =>
        resolve({
          positionData: {
            guid: this.positionGuid() ?? '',
            name: this.positionName() ?? '',
          },
          member: {
            guid: memberGuid,
            firstName: this.memberFirstName() ?? '',
            lastName: this.memberLastName() ?? '',
            email: this.memberEmail() ?? '',
            avatarThumbUrl: this.memberAvatarUrl() ?? null,
          },
        }),
      ),
    );
  }
}

export type TellentUnassignPositionDialogWebComponent = TellentWebComponent<
  TellentUnassignPositionDialogWrapComponent,
  | 'open'
  | 'memberGuid'
  | 'memberFirstName'
  | 'memberLastName'
  | 'memberEmail'
  | 'memberAvatarUrl'
  | 'positionGuid'
  | 'positionName'
  | 'afterClosed'
>;
